import { InvoiceStatus, type TInvoice } from '@/types';
import type { MockSchedule } from '@/pages/offers/mock/mockSplitSchedule';

// A merged row may be a mock installment invoice (no real backend record).
// `__upcoming` marks a scheduled installment that has not been issued yet.
export type MockableInvoice = TInvoice & {
  __mock?: boolean;
  __upcoming?: boolean;
};

export const isMockInvoice = (inv: TInvoice): boolean =>
  (inv as MockableInvoice).__mock === true;

export const isUpcomingInvoice = (inv: TInvoice): boolean =>
  (inv as MockableInvoice).__upcoming === true;

// Clone a base (original) invoice row into a mock installment invoice row.
// `issued=false` produces a read-only "scheduled" preview row.
function mockInstallment(
  base: TInvoice,
  inst: MockSchedule['installments'][number],
  idx: number,
  total: number,
  issued: boolean,
): MockableInvoice {
  const amount = inst.dueAmount ?? 0;
  const paid = issued && inst.paid === true;
  return {
    ...base,
    id: -(base.id * 1000 + idx + 1), // synthetic, stable, non-colliding
    invoiceNumber: `${base.invoiceNumber}-S${idx + 1}`,
    status: paid ? InvoiceStatus.PAID : InvoiceStatus.ISSUED,
    partNumber: idx + 1,
    // Installment amounts reflect what the client pays for this installment,
    // not the original's totals.
    dueAmount: amount,
    paidAmount: paid ? amount : 0,
    remainingAmount: paid ? 0 : amount,
    paidAt: paid ? base.paidAt : null,
    subtotalAmount: amount,
    vatAmount: 0,
    totalAmount: amount,
    dueAt: inst.dueDate ?? base.dueAt,
    aggregationLabel: `${idx + 1}/${total}`,
    __mock: true,
    __upcoming: !issued,
  };
}

/**
 * Mix the mock split invoices into a fetched list (client-side): for every
 * original invoice whose offer has an APPROVED mock schedule, mark it Cancelled
 * and append one row per issued installment. With `includeUpcoming`, un-issued
 * installments are also appended as read-only "scheduled" preview rows (used in
 * the offer's related-invoices context, not the global accounting list).
 */
export function mergeMockInvoices(
  rows: TInvoice[],
  schedules: Record<number, MockSchedule>,
  opts?: { includeUpcoming?: boolean },
): MockableInvoice[] {
  const out: MockableInvoice[] = [];
  for (const row of rows) {
    const sched = row.offerId != null ? schedules[row.offerId] : undefined;
    const approved = sched?.status === 'approved';
    if (row.partNumber == null && approved && sched) {
      out.push({ ...row, status: InvoiceStatus.CANCELLED });
      const total = sched.installments.length;
      sched.installments.forEach((inst, idx) => {
        if (inst.issued) out.push(mockInstallment(row, inst, idx, total, true));
        else if (opts?.includeUpcoming)
          out.push(mockInstallment(row, inst, idx, total, false));
      });
    } else {
      out.push(row);
    }
  }
  return out;
}
